import { useEffect, useState } from "react";
import { api, formatErr, validityStatus, toneClass, downloadCSV, downloadPDF, whatsappShare } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, FileSpreadsheet, Share2, Search } from "lucide-react";

export function PageHeader({ title, subtitle, right }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
      <div className="min-w-0">
        <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-amber-300/80 mb-2">Operations</div>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-2 text-xs sm:text-sm max-w-lg">{subtitle}</p>}
      </div>
      <div className="flex gap-2 flex-wrap md:justify-end">{right}</div>
    </div>
  );
}

export function ExportBar({ rows, filename, whatsappText }) {
  return (
    <div className="flex gap-2 flex-wrap">
      <Button variant="outline" size="sm" data-testid="export-csv" className="border-border bg-[hsl(224_40%_12%)] hover:bg-white/5 gap-2" onClick={() => downloadCSV(rows, `${filename}.csv`)}>
        <FileSpreadsheet className="w-4 h-4" /> Excel
      </Button>
      <Button variant="outline" size="sm" data-testid="export-pdf" className="border-border bg-[hsl(224_40%_12%)] hover:bg-white/5 gap-2" onClick={() => downloadPDF(filename, rows)}>
        <FileText className="w-4 h-4" /> PDF
      </Button>
      <Button variant="outline" size="sm" data-testid="share-whatsapp" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 gap-2" onClick={() => whatsappShare(whatsappText || `${filename}: ${rows.length} records`)}>
        <Share2 className="w-4 h-4" /> WhatsApp
      </Button>
    </div>
  );
}

function EntityForm({ fields, initial, onSubmit, submitLabel = "Save" }) {
  const [form, setForm] = useState(initial || {});
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="grid grid-cols-2 gap-4">
      {fields.map((f) => (
        <div key={f.key} className={f.full ? "col-span-2" : ""}>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}</Label>
          <Input
            type={f.type || "text"} value={form[f.key] ?? ""} onChange={set(f.key)}
            required={f.required}
            data-testid={`field-${f.key}`}
            className="mt-1.5 bg-[hsl(224_40%_12%)] border-border"
          />
        </div>
      ))}
      <div className="col-span-2 flex justify-end pt-2">
        <Button type="submit" data-testid="form-submit" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">{submitLabel}</Button>
      </div>
    </form>
  );
}

export function EntityPage({
  title, subtitle, endpoint, fields, columns, exportFilename,
  extraActions,
}) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    try { const { data } = await api.get(`/${endpoint}`); setRows(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Load failed"); }
  }
  useEffect(() => { load(); }, [endpoint]);

  async function save(form) {
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, form);
      else await api.post(`/${endpoint}`, form);
      toast.success("Saved");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Save failed"); }
  }

  async function del(id) {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/${endpoint}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Delete failed"); }
  }

  const filtered = rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader
        title={title} subtitle={subtitle}
        right={
          <>
            <ExportBar rows={filtered} filename={exportFilename || endpoint} whatsappText={`${title}: ${filtered.length} records`} />
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button data-testid={`add-${endpoint}-btn`} className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2">
                  <Plus className="w-4 h-4" /> New
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-2xl">
                <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit" : "New"} {title.slice(0,-1) || title}</DialogTitle></DialogHeader>
                <EntityForm fields={fields} initial={editing} onSubmit={save} submitLabel={editing ? "Update" : "Create"} />
              </DialogContent>
            </Dialog>
          </>
        }
      />

      <Card className="bg-[hsl(224_45%_9%)] border-border">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input placeholder="Filter records…" value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="filter-input"
            className="bg-transparent border-0 focus-visible:ring-0 h-8" />
          <div className="text-xs text-muted-foreground">{filtered.length} of {rows.length}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
                <th className="text-left p-3 w-10">#</th>
                {columns.map((c) => <th key={c.key} className="text-left p-3">{c.label}</th>)}
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={columns.length + 2} className="p-8 text-center text-muted-foreground">No records</td></tr>
              ) : filtered.map((r, i) => (
                <tr key={r.id} data-testid={`row-${r.id}`} className="border-t border-border hover:bg-white/[0.02] transition-colors">
                  <td className="p-3 text-muted-foreground font-mono">{i + 1}</td>
                  {columns.map((c) => (
                    <td key={c.key} className={`p-3 ${c.mono ? "font-mono text-amber-300" : ""}`}>
                      {c.render ? c.render(r) : (r[c.key] ?? "—")}
                    </td>
                  ))}
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      {extraActions && extraActions(r)}
                      <Button variant="ghost" size="icon" data-testid={`edit-${r.id}`} className="h-8 w-8 hover:bg-white/5" onClick={() => { setEditing(r); setOpen(true); }}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" data-testid={`delete-${r.id}`} className="h-8 w-8 hover:bg-red-500/10 hover:text-red-400" onClick={() => del(r.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export function ValidityBadge({ date }) {
  const s = validityStatus(date);
  return (
    <div className="inline-flex flex-col">
      <span className="font-mono text-xs">{date || "—"}</span>
      <span className={`text-[10px] mt-0.5 px-1.5 py-0.5 rounded border w-fit ${toneClass(s.tone)}`}>{s.label}</span>
    </div>
  );
}

export function VerifiedTag({ ok }) {
  return ok ? (
    <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 text-[10px]">✓ Verified</Badge>
  ) : (
    <Badge className="bg-slate-500/15 text-slate-400 border-slate-500/30 text-[10px]">Pending</Badge>
  );
}
